import { useId, useMemo, useState, type ReactNode } from 'react'
import Icon from '../../docs/Icon'

export type DataColumn<Row> = {
  key: keyof Row & string
  label: string
  /** Sort by this column. Defaults to true. */
  sortable?: boolean
  /** Right-align, for numbers. */
  numeric?: boolean
  render?: (row: Row) => ReactNode
}

export type DataTableProps<Row> = {
  caption: string
  columns: DataColumn<Row>[]
  rows: Row[]
  /** A unique value per row, used for selection. */
  rowKey: keyof Row & string
  /** Show a text filter above the table. */
  filter?: boolean
  /** Checkbox per row, with select all. */
  selectable?: boolean
  /** Keep the header visible while the table scrolls. */
  stickyHeader?: boolean
  striped?: boolean
  /** The header row: the page background (plain), a primary tint, or solid primary. */
  header?: 'plain' | 'tinted' | 'solid'
  /** Bold header labels. */
  headerBold?: boolean
  /** A heavy rule under the header row. */
  headerUnderline?: boolean
  /** Rows per page; pages appear below the table when there are more rows. */
  pageSize?: number
  onSelectionChange?: (keys: string[]) => void
}

type Sort = { key: string; direction: 'ascending' | 'descending' } | null

/**
 * A data table: sortable columns announced with aria-sort, an optional filter,
 * row selection with select all (of the rows on screen), pages, a sticky
 * header, and rows that stack into labeled cards on narrow screens instead of
 * scrolling sideways.
 */
export default function DataTable<Row extends Record<string, unknown>>({
  caption,
  columns,
  rows,
  rowKey,
  filter = false,
  selectable = false,
  stickyHeader = false,
  striped = false,
  header = 'plain',
  headerBold = false,
  headerUnderline = false,
  pageSize,
  onSelectionChange,
}: DataTableProps<Row>) {
  const filterId = useId()
  const [query, setQuery] = useState('')
  const [sort, setSort] = useState<Sort>(null)
  const [selected, setSelected] = useState<string[]>([])
  const [page, setPage] = useState(1)

  const shown = useMemo(() => {
    const words = query.trim().toLowerCase().split(/\s+/).filter(Boolean)
    const matched = words.length
      ? rows.filter((row) => {
          const text = columns.map((column) => String(row[column.key] ?? '')).join(' ').toLowerCase()
          return words.every((word) => text.includes(word))
        })
      : rows
    if (!sort) return matched
    const factor = sort.direction === 'ascending' ? 1 : -1
    return [...matched].sort((a, b) => {
      const x = a[sort.key]
      const y = b[sort.key]
      const order = typeof x === 'number' && typeof y === 'number' ? x - y : String(x).localeCompare(String(y), undefined, { numeric: true })
      return order * factor
    })
  }, [rows, columns, query, sort])

  // A new filter or sort starts again from the first page.
  const pages = pageSize ? Math.max(1, Math.ceil(shown.length / pageSize)) : 1
  const current = Math.min(page, pages)
  const visible = pageSize ? shown.slice((current - 1) * pageSize, current * pageSize) : shown
  const first = shown.length ? (current - 1) * (pageSize ?? shown.length) + 1 : 0

  const keys = visible.map((row) => String(row[rowKey]))
  const allSelected = keys.length > 0 && keys.every((key) => selected.includes(key))
  const someSelected = keys.some((key) => selected.includes(key))

  const select = (next: string[]) => {
    setSelected(next)
    onSelectionChange?.(next)
  }

  const toggleSort = (key: string) => {
    setPage(1)
    setSort((current) =>
      current?.key === key ? { key, direction: current.direction === 'ascending' ? 'descending' : 'ascending' } : { key, direction: 'ascending' },
    )
  }

  return (
    <div className="mds-table-wrap">
      {(filter || selectable || pages > 1) && (
        <div className="mds-table__tools">
          {filter && (
            <label className="mds-table__filter" htmlFor={filterId}>
              <Icon name="filter" size={16} />
              <span className="mds-visually-hidden">Filter {caption.toLowerCase()}</span>
              <input
                id={filterId}
                className="mds-input"
                type="search"
                placeholder="Filter"
                value={query}
                onChange={(event) => {
                setQuery(event.target.value)
                setPage(1)
              }}
              />
            </label>
          )}
          <p className="mds-hint mds-table__count" role="status">
            {pages > 1 ? `${first}-${first + visible.length - 1} of ${shown.length}` : `${shown.length} of ${rows.length}`} shown
            {selectable && selected.length > 0 ? `, ${selected.length} selected` : ''}
          </p>
        </div>
      )}
      <div className={`mds-table-scroll${stickyHeader ? ' mds-table-scroll--sticky' : ''}`} tabIndex={stickyHeader ? 0 : undefined} role={stickyHeader ? 'region' : undefined} aria-label={stickyHeader ? caption : undefined}>
        <table className={`mds-table mds-table--stack${striped ? ' mds-table--striped' : ''}${header === 'plain' ? '' : ` mds-table--header-${header}`}${headerBold ? ' mds-table--header-bold' : ''}${headerUnderline ? ' mds-table--header-underline' : ''}`}>
          <caption className="mds-table__caption">{caption}</caption>
          <thead>
            <tr>
              {selectable && (
                <th scope="col" className="mds-table__select">
                  <input
                    type="checkbox"
                    aria-label="Select all rows"
                    checked={allSelected}
                    ref={(input) => {
                      if (input) input.indeterminate = someSelected && !allSelected
                    }}
                    onChange={() => select(allSelected ? selected.filter((key) => !keys.includes(key)) : [...new Set([...selected, ...keys])])}
                  />
                </th>
              )}
              {columns.map((column) => {
                const active = sort?.key === column.key
                return (
                  <th
                    key={column.key}
                    scope="col"
                    className={column.numeric ? 'is-numeric' : undefined}
                    aria-sort={active ? sort!.direction : column.sortable === false ? undefined : 'none'}
                  >
                    {column.sortable === false ? (
                      column.label
                    ) : (
                      <button type="button" className={`mds-table__sort${active ? ' is-active' : ''}`} onClick={() => toggleSort(column.key)}>
                        {column.label}
                        <Icon name="sort" size={14} />
                      </button>
                    )}
                  </th>
                )
              })}
            </tr>
          </thead>
          <tbody>
            {visible.map((row) => {
              const key = String(row[rowKey])
              const checked = selected.includes(key)
              return (
                <tr key={key} className={checked ? 'is-selected' : undefined}>
                  {selectable && (
                    <td className="mds-table__select" data-label="Select">
                      <input
                        type="checkbox"
                        aria-label={`Select ${String(row[columns[0].key])}`}
                        checked={checked}
                        onChange={() => select(checked ? selected.filter((item) => item !== key) : [...selected, key])}
                      />
                    </td>
                  )}
                  {columns.map((column) => (
                    <td key={column.key} data-label={column.label} className={column.numeric ? 'is-numeric' : undefined}>
                      {column.render ? column.render(row) : String(row[column.key] ?? '')}
                    </td>
                  ))}
                </tr>
              )
            })}
            {shown.length === 0 && (
              <tr>
                <td colSpan={columns.length + (selectable ? 1 : 0)} className="mds-table__empty">
                  No rows match the filter.
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>
      {pages > 1 && (
        <nav className="mds-pagination" aria-label={`${caption} pages`}>
          <button type="button" className="mds-pagination__link" disabled={current === 1} onClick={() => setPage(current - 1)}>
            Previous
          </button>
          {Array.from({ length: pages }, (_, i) => i + 1).map((number) => (
            <button
              key={number}
              type="button"
              className="mds-pagination__link"
              aria-label={`Page ${number}`}
              aria-current={number === current ? 'page' : undefined}
              onClick={() => setPage(number)}
            >
              {number}
            </button>
          ))}
          <button type="button" className="mds-pagination__link" disabled={current === pages} onClick={() => setPage(current + 1)}>
            Next
          </button>
        </nav>
      )}
    </div>
  )
}
